import { useEffect, useMemo, useState } from 'react';

import { EnvironmentOutlined } from '@ant-design/icons';
import { Button, Modal, Segmented, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { formatDecimal, formatInt } from 'scenes/VisitHistory/utils/format';
import { dayHeaderLabel } from 'scenes/VisitHistory/utils/mapData';

import { TiendaPerfectaVisitHistoryStop } from 'models/TiendaPerfecta';

import { DayRoute, buildDayRoutes, buildKpis } from '../utils/storeVisits';
import { ComplianceBar } from './StoreVisitsDaySummary';

const I18N_BASE = 'scenes.store_visits.day_routes';
const ROUTE_REPORT_PATH = '/tienda-perfecta/route-report';

type RouteFilter = 'all' | 'incidents';

interface DayRoutesModalProps {
  date: string | null;
  stops: TiendaPerfectaVisitHistoryStop[];
  timeZone: string;
  onClose: () => void;
  onShowOnMap: (route: DayRoute) => void;
}

function DayRoutesModal({
  date,
  stops,
  timeZone,
  onClose,
  onShowOnMap,
}: DayRoutesModalProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [filter, setFilter] = useState<RouteFilter>('all');
  useEffect(() => setFilter('all'), [date]);

  const noReason = t('scenes.store_visits.incidents.no_reason');
  const dayStops = useMemo(
    () => (date ? stops.filter((s) => s.plannedDate === date) : []),
    [stops, date]
  );
  const routes = useMemo(
    () => (date ? buildDayRoutes(dayStops, date, timeZone, noReason) : []),
    [dayStops, date, timeZone, noReason]
  );
  const kpis = useMemo(() => buildKpis(dayStops), [dayStops]);
  const visible =
    filter === 'incidents' ? routes.filter((r) => r.incidents > 0) : routes;
  const int = (n: number) => formatInt(n, language);

  return (
    <Modal
      open={!!date}
      onCancel={onClose}
      footer={null}
      width={980}
      destroyOnClose
      title={
        date
          ? t(`${I18N_BASE}.title`, { day: dayHeaderLabel(date, language) })
          : ''
      }
    >
      <div className="store-visits-day-routes__summary">
        <span>{t(`${I18N_BASE}.routes`, { count: kpis.routes })}</span>
        <span>
          {t(`${I18N_BASE}.executed`, {
            executed: int(kpis.executed),
            planned: int(kpis.planned),
          })}
        </span>
        <span>{t(`${I18N_BASE}.incidents`, { count: kpis.incidents })}</span>
        <span>
          {t(`${I18N_BASE}.compliance`, {
            pct: formatDecimal(kpis.compliancePct, language),
          })}
        </span>
        <Segmented
          size="small"
          value={filter}
          onChange={(value) => setFilter(value as RouteFilter)}
          options={[
            { value: 'all', label: t(`${I18N_BASE}.filter.all`) },
            { value: 'incidents', label: t(`${I18N_BASE}.filter.incidents`) },
          ]}
        />
      </div>

      <Table<DayRoute>
        rowKey="key"
        size="small"
        dataSource={visible}
        pagination={false}
        scroll={{ x: 860 }}
        locale={{ emptyText: t(`${I18N_BASE}.empty`) }}
        columns={[
          {
            title: t(`${I18N_BASE}.columns.route`),
            key: 'route',
            render: (_, r) => (
              <>
                <strong>{r.routeCode}</strong>
                <div className="store-visits-days__muted">
                  {[r.zoneName, r.distributorName].filter(Boolean).join(' · ')}
                </div>
              </>
            ),
          },
          {
            title: t(`${I18N_BASE}.columns.communes`),
            key: 'communes',
            render: (_, r) => r.communes.slice(0, 3).join(', ') || '—',
          },
          {
            title: t(`${I18N_BASE}.columns.window`),
            key: 'window',
            render: (_, r) =>
              r.start
                ? `${r.start} – ${r.end ?? '…'}`
                : t(`${I18N_BASE}.not_started`),
          },
          {
            title: t(`${I18N_BASE}.columns.executed`),
            key: 'executed',
            align: 'right',
            render: (_, r) => `${int(r.executed)} / ${int(r.planned)}`,
          },
          {
            title: t(`${I18N_BASE}.columns.incidents`),
            key: 'incidents',
            render: (_, r) =>
              r.incidents === 0 ? (
                '—'
              ) : (
                <>
                  <strong>{int(r.incidents)}</strong>
                  <div className="store-visits-days__muted">
                    {r.topIncidents
                      .map((i) => `${i.reason} (${i.count})`)
                      .join(' · ')}
                  </div>
                </>
              ),
          },
          {
            title: t(`${I18N_BASE}.columns.compliance`),
            key: 'compliance',
            width: 160,
            render: (_, r) => <ComplianceBar pct={r.compliancePct} />,
          },
          {
            key: 'map',
            align: 'right',
            render: (_, r) => (
              <Button
                size="small"
                icon={<EnvironmentOutlined />}
                onClick={() => onShowOnMap(r)}
              >
                {t(`${I18N_BASE}.show_on_map`)}
              </Button>
            ),
          },
        ]}
      />

      <div className="store-visits-day-routes__footer">
        {t(`${I18N_BASE}.footer`)}{' '}
        <Link to={ROUTE_REPORT_PATH}>{t(`${I18N_BASE}.route_report`)}</Link>
      </div>
    </Modal>
  );
}

export default DayRoutesModal;
